import React, { useState, useEffect } from 'react';
import './App.css';
import './axiosConfig';
import UploadScreen from './components/UploadScreen';
import ClusteringScreen from './components/ClusteringScreen';
import EditingScreen from './components/EditingScreen';
import ExportScreen from './components/ExportScreen';
import ProjectsScreen from './components/ProjectsScreen';
import LoginScreen from './components/LoginScreen';
import ConfirmEmailScreen from './components/ConfirmEmailScreen';
import AIKernelBuilderScreen from './components/AIKernelBuilderScreen';
import SubscriptionExpiredScreen from './components/SubscriptionExpiredScreen';
import LandingPage from './LandingPage';
import { IconKey, IconCamera, IconSettings, IconFolder, IconClock, IconAlertOctagon, IconAlertTriangle } from './components/Icons';
import axios from 'axios';

// КОНФИГУРАЦИЯ - автоматическое определение API URL
const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5000' 
  : window.location.origin;

function App() {
  const [currentScreen, setCurrentScreen] = useState('upload');
  const [projectData, setProjectData] = useState(null);
  const [viewingSavedProject, setViewingSavedProject] = useState(false);
  const [error, setError] = useState(null);
  
  // Показывать ли лендинг (true) или логин (false) для неавторизованных
  const [showLanding, setShowLanding] = useState(true);
  
  // СОСТОЯНИЕ АУТЕНТИФИКАЦИИ
  const [auth, setAuth] = useState(() => {
    const savedAuth = localStorage.getItem('auth');
    if (savedAuth) {
      const parsed = JSON.parse(savedAuth);
      // Устанавливаем заголовок сразу при инициализации
      if (parsed && parsed.token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${parsed.token}`;
      }
      return parsed;
    }
    return null;
  });

  // Состояние для загрузки аватара
  const [showAvatarUpload, setShowAvatarUpload] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Состояние для управления API ключом
  const [showApiKeySettings, setShowApiKeySettings] = useState(false);
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiApiKeyStatus, setAiApiKeyStatus] = useState(null); // null, 'saved', 'testing', 'error'
  const [aiApiKeyMessage, setAiApiKeyMessage] = useState('');

  // Состояние для подтверждения email
  const [confirmToken, setConfirmToken] = useState(null);
  const [prefillEmail, setPrefillEmail] = useState(null);
  
  // Состояние подписки (trial)
  const [subscription, setSubscription] = useState(null);

  // Проверка URL на наличие токена подтверждения
  useEffect(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const queryToken = urlParams.get('confirm');
    
    console.log('Current path:', path);
    console.log('Query token:', queryToken);

    if (queryToken) {
      setConfirmToken(queryToken);
    } else if (path.includes('/confirm/')) {
      const parts = path.split('/confirm/');
      const token = parts[parts.length - 1];
      console.log('Found path token:', token);
      if (token) {
        setConfirmToken(token);
      }
    }
  }, []);

  const handleBackToLogin = () => {
    setConfirmToken(null);
    window.history.pushState({}, '', '/');
  };

  // Настройка axios при изменении auth
  useEffect(() => {
    if (auth && auth.token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${auth.token}`;
      localStorage.setItem('auth', JSON.stringify(auth));
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('auth');
    }
  }, [auth]);

  const handleLoginSuccess = (token, user, apiKey, subStatus = null) => {
    setAuth({ token, user, apiKey });
    setSubscription(subStatus);
  };

  const handleLogout = () => {
    setAuth(null);
    setShowLanding(true);
    handleNewProject();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Проверка размера (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Файл слишком большой. Максимум 5MB');
      return;
    }

    // Проверка типа
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Недопустимый формат. Используйте: PNG, JPG, GIF, WEBP');
      return;
    }

    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/upload-avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Обновляем аватар в состоянии
        const newAvatarUrl = `${API_BASE_URL}${response.data.avatar_url}`;
        setAuth(prev => ({
          ...prev,
          user: {
            ...prev.user,
            picture: newAvatarUrl
          }
        }));
        
        // Обновляем в localStorage
        const savedAuth = JSON.parse(localStorage.getItem('auth'));
        savedAuth.user.picture = newAvatarUrl;
        localStorage.setItem('auth', JSON.stringify(savedAuth));
        
        setShowAvatarUpload(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка загрузки аватара');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Функции для управления API ключом
  const handleSaveApiKey = async () => {
    if (!aiApiKey.trim()) {
      setAiApiKeyStatus('error');
      setAiApiKeyMessage('Введите API ключ');
      return;
    }
    
    setAiApiKeyStatus('testing');
    setAiApiKeyMessage('Проверка ключа...');
    
    try {
      // Сначала тестируем ключ
      const testResponse = await axios.post(`${API_BASE_URL}/api/user/ai-api-key`, {
        action: 'test',
        api_key: aiApiKey.trim()
      }, {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      
      if (testResponse.data.success) {
        // Ключ работает, сохраняем
        const saveResponse = await axios.post(`${API_BASE_URL}/api/user/ai-api-key`, {
          action: 'save',
          api_key: aiApiKey.trim()
        }, {
          headers: { 'Authorization': `Bearer ${auth.token}` }
        });
        
        if (saveResponse.data.success) {
          setAiApiKeyStatus('saved');
          setAiApiKeyMessage('API ключ сохранен и работает!');
          // Очищаем поле для безопасности
          setAiApiKey('');
        }
      }
    } catch (err) {
      setAiApiKeyStatus('error');
      setAiApiKeyMessage(err.response?.data?.error || 'Ошибка проверки ключа');
    }
  };
  
  const handleDeleteApiKey = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/ai-api-key`, {
        action: 'delete'
      }, {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      
      if (response.data.success) {
        setAiApiKeyStatus('saved');
        setAiApiKeyMessage('API ключ удален. Будет использоваться системный ключ.');
      }
    } catch (err) {
      setAiApiKeyStatus('error');
      setAiApiKeyMessage(err.response?.data?.error || 'Ошибка удаления ключа');
    }
  };
  
  const loadSubscriptionStatus = async () => {
    if (!auth || !auth.token) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/user/subscription-status`, {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      if (response.data.success) {
        setSubscription(response.data.subscription);
      }
    } catch (err) {
      console.error('Error loading subscription status:', err);
    }
  };

  const loadApiKeyStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/user/ai-api-key`, {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      
      if (response.data.success && response.data.has_key) {
        setAiApiKeyStatus('saved');
        setAiApiKeyMessage(`Сохранен ключ: ${response.data.key_preview}`);
      }
    } catch (err) {
      console.error('Error loading API key status:', err);
    }
  };

  const handleUploadComplete = (data) => {
    console.log('Upload complete, data:', data);
    
    if (data.success) {
      // Сохраняем данные проекта
      setProjectData(data);
      // Переходим к кластеризации
      setCurrentScreen('clustering');
    } else {
      setError(data.error || 'Ошибка при создании проекта');
    }
  };

  const handleClusteringComplete = (clusteringResult) => {
    console.log('Clustering complete, data:', clusteringResult);
    
    // Обновляем данные проекта с результатами кластеризации
    setProjectData(prev => ({
      ...prev,
      clusters: clusteringResult.clusters,
      n_clusters: clusteringResult.n_clusters,
      text_count: clusteringResult.text_count
    }));
    
    setCurrentScreen('editing');
  };

  const handleEditingComplete = (categories) => {
    console.log('Editing complete, categories:', categories);
    
    setProjectData(prev => ({
      ...prev,
      categories: categories
    }));
    
    setViewingSavedProject(false);
    setCurrentScreen('export');
  };

  const handleBack = () => {
    console.log('Going back from:', currentScreen);
    
    if (currentScreen === 'clustering') {
      setCurrentScreen('upload');
    } else if (currentScreen === 'editing') {
      setCurrentScreen('clustering');
    } else if (currentScreen === 'export') {
      setCurrentScreen('editing');
    }
  };

  const handleNewProject = () => {
    console.log('Starting new project');
    
    setProjectData(null);
    setViewingSavedProject(false);
    setCurrentScreen('upload');
    setError(null);
  };

  const handleOpenProject = async (projectId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/project/${projectId}`);
      if (response.data.success) {
        const proj = response.data.project;
        setProjectData({
          project_id: proj.id,
          project: proj,
          project_name: proj.name,
          text_count: proj.text_count,
          n_clusters: proj.n_clusters,
          clusters: proj.clusters,
          categories: proj.categories,
          industry: proj.industry
        });
        setViewingSavedProject(true);
        setCurrentScreen('export');
      } else {
        setError(response.data.error || 'Не удалось открыть проект');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при открытии проекта');
    }
  };

  const handleCheckAPI = async () => {
    try {
      const headers = {};
      if (auth && auth.token) {
        headers['Authorization'] = `Bearer ${auth.token}`;
      }
      const response = await fetch(`${API_BASE_URL}/api/health`, { headers });
      const data = await response.json();
      alert(`API работает!\nСтатус: ${data.status}\nСервис: ${data.service}`);
    } catch (err) {
      alert(`API не доступен. Проверьте:\n1. Запущен ли бэкенд?\n2. Адрес: ${API_BASE_URL}\n3. Нет ли ошибок в консоли бэкенда`);
    }
  };

  // Загружаем статус подписки при авторизации
  useEffect(() => {
    if (auth) {
      loadSubscriptionStatus();
    }
  }, [auth]);

  // Проверим подключение при загрузке приложения (отключено по просьбе пользователя)
  React.useEffect(() => {
    console.log('App mounted, checking API connection...');
    // handleCheckAPI();
  }, []);

  // Лендинг — самостоятельная страница без шапки и футера приложения
  if (!auth && !confirmToken && showLanding) {
    return <LandingPage onGetStarted={() => setShowLanding(false)} />;
  }

  return (
    <div className="App">
        <header className="App-header">
          <div className="header-content">
            <div className="header-logo-section">
              <div
                className="header-brand"
                onClick={() => { if (!auth) setShowLanding(true); }}
                style={{ cursor: !auth ? 'pointer' : 'default' }}
                title={!auth ? 'На главную' : undefined}
              >
                <img src="/logo_openend.png" alt="OpenEnd Clusters" className="nav-logo" />
                <span className="nav-brand-name">OpenEnd <span className="nav-brand-accent">Clusters</span></span>
              </div>

              {auth ? (
                <div className="user-profile-header" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  padding: '8px 20px',
                  backgroundColor: 'var(--panel)',
                  border: '1px solid var(--border)',
                  borderRadius: '30px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  justifySelf: 'end'
                }}>
                <div style={{ position: 'relative', cursor: auth.user.picture ? 'default' : 'pointer' }} onClick={() => !auth.user.picture && setShowAvatarUpload(!showAvatarUpload)}>
                  {auth.user.picture && auth.user.picture.trim() !== '' && auth.user.picture !== 'null' ? (
                    <img 
                      src={auth.user.picture.startsWith('http') ? auth.user.picture : `${API_BASE_URL}${auth.user.picture}`} 
                      alt=""
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        console.error('Avatar image failed to load:', auth.user.picture, e);
                      }}
                      style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} 
                    />
                  ) : (
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: '#007bff',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}>
                      {auth.user.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {!auth.user.picture && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: '14px',
                      height: '14px',
                      backgroundColor: 'var(--panel)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }}>
                      <IconCamera size={9} />
                    </div>
                  )}
                </div>
                
                <div style={{ color: 'var(--text)', fontSize: '14px' }}>
                  <div style={{ fontWeight: 'bold' }}>{auth.user.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{auth.user.email}</div>
                </div>
                
                <button 
                  onClick={() => {
                    setShowApiKeySettings(!showApiKeySettings);
                    if (!showApiKeySettings) loadApiKeyStatus();
                  }}
                  style={{
                    backgroundColor: 'rgba(100, 181, 246, 0.15)',
                    border: '1px solid rgba(100, 181, 246, 0.45)',
                    color: '#64b5f6',
                    padding: '6px 12px',
                    borderRadius: '15px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                  title="Настройки API ИИ"
                >
                  <IconKey size={13} style={{ marginRight: '5px', marginTop: '-2px' }} />API
                </button>

                <button 
                  onClick={handleLogout}
                  style={{
                    backgroundColor: 'var(--panel-2)',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    padding: '6px 12px',
                    borderRadius: '15px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  Выйти
                </button>

                {showApiKeySettings && (
                  <div style={{
                    position: 'absolute',
                    top: '50px',
                    right: '80px',
                    backgroundColor: 'var(--panel)',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                    zIndex: 1001,
                    minWidth: '320px',
                    color: 'var(--text)',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ marginBottom: '15px', fontSize: '16px', fontWeight: 'bold', color: 'var(--text)' }}>
                      <IconKey size={16} style={{ marginRight: '8px', marginTop: '-2px' }} />Настройки API ИИ
                    </div>
                    
                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '15px', lineHeight: '1.5' }}>
                      Для использования собственного API ключа DeepSk:
                      <br/>1. Получите ключ на <a href="https://platform.deepseek.com" target="_blank" rel="noopener noreferrer" style={{color: '#64b5f6'}}>platform.deepseek.com</a>
                      <br/>2. Вставьте ключ ниже (начинается с "sk-")
                      <br/>3. Если ключ не задан, используется системный
                    </div>

                    <input
                      type="password"
                      placeholder="sk-... (ваш API ключ)"
                      value={aiApiKey}
                      onChange={(e) => setAiApiKey(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        fontSize: '13px',
                        marginBottom: '10px',
                        boxSizing: 'border-box'
                      }}
                    />

                    {aiApiKeyMessage && (
                      <div style={{
                        fontSize: '12px',
                        padding: '8px',
                        borderRadius: '6px',
                        marginBottom: '10px',
                        backgroundColor: aiApiKeyStatus === 'error' ? 'rgba(244, 67, 54, 0.15)' : 
                                        aiApiKeyStatus === 'saved' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(100, 181, 246, 0.15)',
                        color: aiApiKeyStatus === 'error' ? '#ff8a80' : 
                               aiApiKeyStatus === 'saved' ? '#7bd88a' : '#90caf9'
                      }}>
                        {aiApiKeyMessage}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={handleSaveApiKey}
                        disabled={aiApiKeyStatus === 'testing'}
                        style={{
                          flex: 1,
                          padding: '10px',
                          backgroundColor: aiApiKeyStatus === 'testing' ? 'var(--border)' : '#64b5f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: aiApiKeyStatus === 'testing' ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        {aiApiKeyStatus === 'testing' ? 'Проверка...' : 'Сохранить'}
                      </button>
                      
                      <button
                        onClick={handleDeleteApiKey}
                        style={{
                          padding: '10px',
                          backgroundColor: 'var(--panel-2)',
                          color: 'var(--muted)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                )}

                {showAvatarUpload && (
                  <div style={{
                    position: 'absolute',
                    top: '50px',
                    right: '0',
                    backgroundColor: 'var(--panel)',
                    padding: '15px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.45)',
                    zIndex: 1000,
                    minWidth: '200px',
                    color: 'var(--text)'
                  }}>
                    <div style={{ marginBottom: '10px', fontSize: '14px', fontWeight: 'bold', color: 'var(--text)' }}>
                      Изменить аватар
                    </div>
                    <input 
                      type="file" 
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleAvatarUpload}
                      disabled={uploadingAvatar}
                      style={{ 
                        fontSize: '12px',
                        color: 'var(--text)',
                        cursor: 'pointer'
                      }}
                    />
                    {uploadingAvatar && (
                      <div style={{ marginTop: '10px', fontSize: '12px', color: '#64b5f6', fontWeight: 'bold' }}>
                        Загрузка...
                      </div>
                    )}
                  </div>
                )}
              </div>
              ) : (
                <div></div>
              )}
            </div>
          </div>

          {auth && (
            <div className="progress-steps">
              <div className={`step ${currentScreen === 'upload' ? 'active' : ''}`}>
                <div className="step-number">1</div>
                <div className="step-label">Загрузка</div>
              </div>
              <div className={`step ${currentScreen === 'clustering' ? 'active' : ''}`}>
                <div className="step-number">2</div>
                <div className="step-label">Кластеризация</div>
              </div>
              <div className={`step ${currentScreen === 'editing' ? 'active' : ''}`}>
                <div className="step-number">3</div>
                <div className="step-label">Редактирование</div>
              </div>
              <div className={`step ${currentScreen === 'export' ? 'active' : ''}`}>
                <div className="step-number">4</div>
                <div className="step-label">Экспорт</div>
              </div>
              <div className={`step ${currentScreen === 'kernel-builder' ? 'active' : ''}`} style={{cursor: 'pointer'}} onClick={() => setCurrentScreen('kernel-builder')}>
                <div className="step-number"><IconSettings size={16} /></div>
                <div className="step-label">Разработчик ядра ИИ</div>
              </div>
              {auth?.user?.is_admin && (
              <div className={`step ${currentScreen === 'projects' ? 'active' : ''}`} style={{cursor: 'pointer'}} onClick={() => setCurrentScreen('projects')}>
                <div className="step-number"><IconFolder size={16} /></div>
                <div className="step-label">Проекты</div>
              </div>
              )}
            </div>
          )}
        </header>

        {/* Баннер пробного периода */}
        {auth && subscription && subscription.trial_active && (
          <div className="trial-banner">
            <span className="trial-icon"><IconClock size={18} /></span>
            <span className="trial-text">
              Пробный период: осталось <strong>{subscription.days_left} {subscription.days_left === 1 ? 'день' : subscription.days_left < 5 ? 'дня' : 'дней'}</strong>
            </span>
            <span className="trial-hint">После истечения требуется подписка</span>
          </div>
        )}

        {/* Баннер истекшего пробного периода */}
        {auth && subscription && !subscription.can_use && (
          <div className="trial-expired-banner">
            <span className="trial-icon"><IconAlertOctagon size={20} /></span>
            <span className="trial-text">
              Пробный период истек. Приобретите подписку для продолжения работы.
            </span>
          </div>
        )}

        <main className="App-main">
          {error && (
            <div className="error-banner">
              <span className="error-icon"><IconAlertTriangle size={18} /></span>
              <span className="error-message">{error}</span>
              <button className="error-close" onClick={() => setError(null)}>×</button>
            </div>
          )}

          {confirmToken ? (
            <ConfirmEmailScreen 
              token={confirmToken} 
              apiBaseUrl={API_BASE_URL} 
              onBackToLogin={handleBackToLogin}
              onConfirmed={(email) => {
                setPrefillEmail(email);
                setConfirmToken(null);
                window.history.replaceState({}, document.title, '/');
              }}
            />
          ) : !auth ? (
            <LoginScreen 
              onLoginSuccess={handleLoginSuccess}
              apiBaseUrl={API_BASE_URL}
              prefillEmail={prefillEmail}
            />
          ) : (
            <>
              {subscription && !subscription.can_use ? (
                <SubscriptionExpiredScreen onLogout={handleLogout} />
              ) : (
                <>
                  {currentScreen === 'upload' && (
                    <UploadScreen
                      onComplete={handleUploadComplete}
                      onError={setError}
                      apiBaseUrl={API_BASE_URL}
                      authToken={auth?.token}
                    />
                  )}

              {currentScreen === 'clustering' && projectData && (
                <ClusteringScreen
                  projectData={projectData}
                  onComplete={handleClusteringComplete}
                  onBack={handleBack}
                  onError={setError}
                  apiBaseUrl={API_BASE_URL}
                />
              )}

              {currentScreen === 'editing' && projectData && (
                <EditingScreen
                  projectData={projectData}
                  onComplete={handleEditingComplete}
                  onBack={handleBack}
                  onError={setError}
                  apiBaseUrl={API_BASE_URL}
                />
              )}

              {currentScreen === 'export' && projectData && (
                <ExportScreen
                  projectData={projectData}
                  onBack={viewingSavedProject ? () => setCurrentScreen('projects') : handleBack}
                  onNewProject={handleNewProject}
                  onError={setError}
                  apiBaseUrl={API_BASE_URL}
                  onGoToKernelBuilder={() => setCurrentScreen('kernel-builder')}
                />
              )}

              {currentScreen === 'projects' && auth?.user?.is_admin && (
                <ProjectsScreen
                  apiBaseUrl={API_BASE_URL}
                  onOpen={handleOpenProject}
                  onError={setError}
                />
              )}

              {currentScreen === 'kernel-builder' && auth && (
                <AIKernelBuilderScreen
                  token={auth.token}
                  onBack={() => setCurrentScreen('upload')}
                  onKernelsUpdated={(kernels) => {
                    console.log('Kernels updated:', kernels);
                    // Можно сохранить в localStorage или state
                  }}
                />
              )}

              {!projectData && currentScreen !== 'upload' && currentScreen !== 'projects' && currentScreen !== 'kernel-builder' && (
                <div className="no-project-message">
                  <h2>Проект не найден</h2>
                  <p>Пожалуйста, начните с загрузки файла.</p>
                  <button 
                    className="primary-button"
                    onClick={handleNewProject}
                  >
                    Начать новый проект
                  </button>
                </div>
              )}

              <div className="global-controls" style={{ display: 'flex', gap: '10px', justifyContent: 'center', margin: '30px 0 10px 0' }}>
                <button className="secondary-button" onClick={handleCheckAPI}>
                  Проверить API
                </button>
                <button className="secondary-button" onClick={handleNewProject}>
                  Новый проект
                </button>
              </div>
            </>
          )}
            </>
          )}
        </main>

        <footer className="App-footer">
          <p>OpenEnd Clusters — инструмент для анализа и кодирования открытых вопросов • Версия 1.0</p>
          <p style={{ marginTop: '5px', fontSize: '12px', color: 'white' }}>
            &copy; ООО "КреаМетрикс Рисеч", 2026. Все права защищены.
          </p>
          <div className="footer-links" style={{ justifyContent: 'center' }}>
            <div className="api-status">
              API: <span className="status-dot">●</span> {API_BASE_URL.replace('https://', '').replace('http://', '')}
            </div>
          </div>
        </footer>
      </div>
  );
}

export default App;
